import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { IonicPage, NavController, NavParams, LoadingController, ModalController, AlertController, Platform, ActionSheetController, Events } from 'ionic-angular';
import { TranslateService } from '@ngx-translate/core';
import { ServerService } from '../../services/server-service';
import { ToastService } from '../../services/toast-service';
import swal from 'sweetalert';
import { InAppBrowser } from '@ionic-native/in-app-browser';
import { TabsPage } from '../tabs/tabs';

@IonicPage()
@Component({
  selector: 'page-login',
  templateUrl: 'login.html',
})
export class LoginPage implements OnInit {

  loginForm: FormGroup;
  segment: any = 'home';
  showPassword: boolean = false;
  isResetMode: boolean = false;
  authMethod: 'password' | 'phone' = 'password';
  phoneLoginStep: number = 1;
  wiggleFieldName: string = '';
  /** When set, only this field shows a validation error (ordered submit flow). */
  displayErrorField: string = '';
  selectedLanguage = 'he';
  private phoneLoginContext: any = null;

  readonly validationMessages: { [key: string]: { type: string; messageKey: string }[] } = {
    matnas: [
      { type: 'required', messageKey: 'LoginPage.validation.matnasRequired' },
    ],
    mail: [
      { type: 'required', messageKey: 'LoginPage.validation.mailRequired' },
      { type: 'email', messageKey: 'LoginPage.validation.mailInvalid' },
    ],
    password: [
      { type: 'required', messageKey: 'LoginPage.validation.passwordRequired' },
    ],
    phone: [
      { type: 'required', messageKey: 'LoginPage.validation.phoneRequired' },
      { type: 'pattern', messageKey: 'LoginPage.validation.phoneInvalid' },
    ],
    otpCode: [
      { type: 'required', messageKey: 'LoginPage.validation.otpRequired' },
      { type: 'pattern', messageKey: 'LoginPage.validation.otpPattern' },
    ],
  };

  constructor(
    public navCtrl: NavController,
    public navParams: NavParams,
    private api: ServerService,
    private toast: ToastService,
    public loadingCtrl: LoadingController,
    public modalCtrl: ModalController,
    private alertCtrl: AlertController,
    public plt: Platform,
    private iab: InAppBrowser,
    private actionSheetCtrl: ActionSheetController,
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private events: Events,
  ) {
    this.selectedLanguage = localStorage.getItem('language') || this.translate.currentLang || 'he';
  }

  private readonly iosDevice = this.detectIosDevice();

  get isLtr(): boolean {
    return this.selectedLanguage === 'en';
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      matnas: [''],
      mail: [''],
      password: [''],
      phone: [''],
      otpCode: [''],
    });
    this.updateFormValidators();
  }

  onLanguageChange(ev: any) {
    const lang = ev && ev.target ? ev.target.value : '';
    if (!lang || lang === this.selectedLanguage) {
      return;
    }
    this.setLanguage(lang);
  }

  private setLanguage(lang: string) {
    this.selectedLanguage = lang;
    this.translate.use(lang);
    localStorage.setItem('language', lang);
    this.events.publish('changeLanguage', lang);
    this.toast.presentToast(this.translate.instant('SettingsPage.popover.saved'));
  }

  segmentChanged(ev: any) {
  }

  isPhoneLogin(): boolean {
    return this.authMethod === 'phone';
  }

  setAuthMethod(method: 'password' | 'phone'): void {
    this.authMethod = method;
    this.isResetMode = false;
    this.showPassword = false;
    this.phoneLoginStep = 1;
    this.phoneLoginContext = null;
    if (!this.loginForm) {
      return;
    }
    this.loginForm.patchValue({
      password: '',
      phone: '',
      otpCode: '',
    });
    this.updateFormValidators();
  }

  isFieldInvalid(fieldName: string): boolean {
    if (!this.loginForm) {
      return false;
    }
    const control = this.loginForm.get(fieldName);
    if (!control || !control.invalid) {
      return false;
    }
    if (this.displayErrorField) {
      return this.displayErrorField === fieldName;
    }
    return control.dirty || control.touched;
  }

  getFieldError(fieldName: string): string {
    if (!this.loginForm) {
      return '';
    }
    const control = this.loginForm.get(fieldName);
    const messages = this.validationMessages[fieldName];
    if (!control || !messages) {
      return '';
    }
    for (const validation of messages) {
      if (control.hasError(validation.type)) {
        return this.translate.instant(validation.messageKey);
      }
    }
    return '';
  }

  getPrimaryButtonLabel(): string {
    if (this.isResetMode) {
      return this.translate.instant('LoginPage.sendReset');
    }
    if (this.isPhoneLogin()) {
      return this.phoneLoginStep === 1
        ? this.translate.instant('LoginPage.sendOtp')
        : this.translate.instant('LoginPage.verifyLogin');
    }
    return this.translate.instant('LoginPage.login');
  }

  private requiredNotEmptyValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || String(value).trim() === '') {
      return { required: true };
    }
    return null;
  }

  private updateFormValidators(): void {
    if (!this.loginForm) {
      return;
    }
    const matnas = this.loginForm.get('matnas');
    const mail = this.loginForm.get('mail');
    const password = this.loginForm.get('password');
    const phone = this.loginForm.get('phone');
    const otpCode = this.loginForm.get('otpCode');

    matnas.setValidators([this.requiredNotEmptyValidator]);
    mail.setValidators([this.requiredNotEmptyValidator, Validators.email]);
    password.clearValidators();
    phone.clearValidators();
    otpCode.clearValidators();

    if (!this.isResetMode && !this.isPhoneLogin()) {
      password.setValidators([this.requiredNotEmptyValidator]);
    }

    if (this.isPhoneLogin()) {
      phone.setValidators([Validators.required, Validators.pattern(/^0\d{8,9}$/)]);
      if (this.phoneLoginStep === 2) {
        otpCode.setValidators([Validators.required, Validators.pattern(/^[0-9]{5}$/)]);
      }
    }

    matnas.updateValueAndValidity({ emitEvent: false });
    mail.updateValueAndValidity({ emitEvent: false });
    password.updateValueAndValidity({ emitEvent: false });
    phone.updateValueAndValidity({ emitEvent: false });
    otpCode.updateValueAndValidity({ emitEvent: false });
  }

  private shouldValidateField(fieldName: string): boolean {
    if (fieldName === 'password') {
      return !this.isResetMode && !this.isPhoneLogin();
    }
    if (fieldName === 'phone') {
      return this.isPhoneLogin();
    }
    if (fieldName === 'otpCode') {
      return this.isPhoneLogin() && this.phoneLoginStep === 2;
    }
    return true;
  }

  isFieldWiggling(fieldName: string): boolean {
    return this.wiggleFieldName === fieldName;
  }

  private triggerFieldWiggle(fieldName: string): void {
    this.wiggleFieldName = '';
    setTimeout(() => {
      this.wiggleFieldName = fieldName;
      setTimeout(() => {
        if (this.wiggleFieldName === fieldName) {
          this.wiggleFieldName = '';
        }
      }, 480);
    }, 0);
  }

  private resetValidationDisplayState(): void {
    this.displayErrorField = '';
    if (!this.loginForm) {
      return;
    }
    const fieldNames = ['matnas', 'mail', 'password', 'phone', 'otpCode'];
    for (const fieldName of fieldNames) {
      const control = this.loginForm.get(fieldName);
      if (control) {
        control.markAsUntouched();
        control.markAsPristine();
      }
    }
  }

  /** Validates fields one by one; stops at the first invalid field. */
  private validateFieldsInOrder(fieldNames: string[]): boolean {
    if (!this.loginForm) {
      return false;
    }
    this.resetValidationDisplayState();
    this.updateFormValidators();

    for (const fieldName of fieldNames) {
      if (!this.shouldValidateField(fieldName)) {
        continue;
      }

      const control = this.loginForm.get(fieldName);
      if (!control) {
        continue;
      }

      control.updateValueAndValidity({ emitEvent: false });

      if (control.valid) {
        continue;
      }

      this.displayErrorField = fieldName;
      control.markAsTouched();
      this.triggerFieldWiggle(fieldName);
      return false;
    }

    this.displayErrorField = '';
    return true;
  }

  onPrimaryAction(): void {
    if (this.isResetMode) {
      this.resetPassword();
      return;
    }

    if (this.isPhoneLogin()) {
      if (this.phoneLoginStep === 1) {
        this.startPhoneLoginStep();
      } else {
        this.verifyPhoneOtpLogin();
      }
      return;
    }

    this.logForm();
  }

  private finalizeLogin(response: any): void {
    const row = this.extractUserRow(response);
    if (!row) {
      this.toast.presentToast(this.translate.instant('LoginPage.userLoadError'));
      return;
    }

    const matnas = this.loginForm.get('matnas').value;
    if (matnas == "בית עמנואל") {
      localStorage.setItem("beitEmonoalRamatGanServer", "1");
    } else {
      localStorage.setItem("beitEmonoalRamatGanServer", "0");
    }

    localStorage.setItem('mercazName', row.mercazName);
    localStorage.setItem('userName', row.workerName);
    localStorage.setItem('user_id', row.workerID);
    localStorage.setItem('mercazAllId', row.mercazId);
    localStorage.setItem('mercazImage', row.mercazImage);
    localStorage.setItem('server', row.server);
    localStorage.setItem('mercazType', row.mercazType);

    this.navCtrl.setRoot(TabsPage);
    this.loguserFunction();
  }

  private extractUserRow(response: any): any {
    if (response && response['userID'] && response['userID'][0]) {
      return response['userID'][0];
    }
    if (response && response['userData']) {
      return response['userData'];
    }
    return null;
  }

  private startPhoneLoginStep(): void {
    if (!this.validateFieldsInOrder(['matnas', 'mail', 'phone'])) {
      return;
    }

    const formValue = this.loginForm.value;
    const loading = this.loadingCtrl.create({ content: this.translate.instant('LoginPage.loadingSendOtp') });
    loading.present();

    const step1Object = {
      "mercazName": formValue.matnas,
      "userName": formValue.mail,
      "phone": formValue.phone
    };

    this.api.sendPostParams('/login/phone-step1.php', step1Object).then((step1Response: any) => {
      loading.dismiss();
      if (step1Response && step1Response['sucsses']) {
        this.phoneLoginContext = step1Response['userData'] || null;
        const alertBox = this.alertCtrl.create({
          title: this.translate.instant('LoginPage.phoneVerifyTitle'),
          subTitle: this.translate.instant('LoginPage.phoneVerifySubtitle'),
          enableBackdropDismiss: false,
          inputs: [
            {
              name: 'otpCode',
              placeholder: this.translate.instant('LoginPage.otp'),
              type: 'tel',
              id: 'autofocus'
            }
          ],
          buttons: [
            {
              text: this.translate.instant('COMMON.cancel'),
              role: 'cancel'
            },
            {
              text: this.translate.instant('COMMON.continue'),
              handler: data => {
                const otpRegex = /^[0-9]{5}$/;
                if (!data.otpCode) {
                  this.showOtpInputErrorAlert(this.translate.instant('LoginPage.validation.otpRequired'));
                  return false;
                }
                if (!otpRegex.test(data.otpCode)) {
                  this.showOtpInputErrorAlert(this.translate.instant('LoginPage.validation.otpPattern'));
                  return false;
                }

                this.loginForm.patchValue({ otpCode: data.otpCode });
                this.verifyPhoneOtpLogin();
                return true;
              }
            }
          ]
        });
        alertBox.present().then(() => {
          const autofocus = document.getElementById('autofocus') as HTMLElement;
          if (autofocus) {
            autofocus.focus();
          }

          const otpInput = document.getElementById('autofocus');
          if (otpInput) {
            otpInput.setAttribute('autocomplete', 'one-time-code');
          }
        });
      } else if (step1Response && step1Response['error']) {
        this.toast.presentToast(step1Response['error']);
      } else {
        this.toast.presentToast(this.translate.instant('LoginPage.otpStep1Error'));
      }
    }, (err => {
      loading.dismiss();
      console.log("phone login step1 error:", JSON.stringify(err));
      this.toast.presentHttpError(err);
    }));
  }

  private showOtpInputErrorAlert(message: string): void {
    const alertBox = this.alertCtrl.create({
      title: this.translate.instant('COMMON.error'),
      subTitle: message,
      buttons: [this.translate.instant('COMMON.ok')]
    });
    alertBox.present();
  }

  private verifyPhoneOtpLogin(): void {
    if (!this.validateFieldsInOrder(['matnas', 'mail', 'phone', 'otpCode'])) {
      return;
    }

    const formValue = this.loginForm.value;
    const loading = this.loadingCtrl.create({ content: this.translate.instant('LoginPage.loadingVerifyOtp') });
    loading.present();

    const object = {
      "mercazName": formValue.matnas,
      "userName": formValue.mail,
      "phone": formValue.phone,
      "otpCode": formValue.otpCode,
      "userData": this.phoneLoginContext
    };

    this.api.sendPostParams('/login/phone-step2.php', object).then((response: any) => {
      loading.dismiss();
      if (response && response['sucsses'] && response['message'] === 'loginOK' && response['userID'] && response['userID'][0]) {
        this.finalizeLogin(response);
      } else {
        this.toast.presentToast(this.translate.instant('LoginPage.wrongOtp'));
      }
    }, (err => {
      loading.dismiss();
      console.log("phone login step2 error:", JSON.stringify(err));
      this.toast.presentHttpError(err);
    }));
  }

  logForm() {
    if (!this.validateFieldsInOrder(['matnas', 'mail', 'password'])) {
      return;
    }

    const formValue = this.loginForm.value;
    let loading = this.loadingCtrl.create({ content: this.translate.instant('COMMON.loading') });
    loading.present();
    let object = {
      "mercazName": formValue.matnas,
      "userName": formValue.mail,
      "password": formValue.password,
    };
    console.log("object:", object);

    this.api.auth('/login/index.php', object).then((response: any) => {
      loading.dismiss();
      if (response['sucsses']) {
        this.finalizeLogin(response);
      } else {
        this.toast.presentToast(this.translate.instant('LoginPage.wrongCredentials'));
        this.loginForm.patchValue({ password: '' });
        this.loginForm.get('password').markAsUntouched();
      }

    }, (err => {
      loading.dismiss();
      console.log("error2:", JSON.stringify(err));
      this.toast.presentHttpError(err);
    }));
  }

  showSwal(title, text, type) {
    swal({
      icon: type,
      timer: 3000,
      title: title,
      text: text,
      buttons: ['']
    });
  }

  callSupport() {
    const browser = this.iab.create("tel:048727969", '_system');
  }

  loguserFunction() {
    let object = {
      "personalID": localStorage.getItem('user_id'),
      "mercazAllId": localStorage.getItem('mercazAllId'),
      "pqpq": localStorage.getItem('server'),
      "pushToken": localStorage.getItem('push'),
    };
    console.log("object:", object);

    this.api.sendPostParams('/log/logUser.php', object).then((data) => {
    }, (err => {
      console.log("error2:", JSON.stringify(err));
    }));
  }

  onPasswordToggle(): void {
    this.showPassword = !this.showPassword;
  }

  toggleResetMode(): void {
    if (this.isPhoneLogin()) {
      return;
    }
    this.isResetMode = !this.isResetMode;
    if (!this.isResetMode) {
      this.loginForm.patchValue({ password: '' });
    }
    this.updateFormValidators();
  }

  resetPassword(): void {
    if (!this.validateFieldsInOrder(['matnas', 'mail'])) {
      return;
    }

    const formValue = this.loginForm.value;
    let loading = this.loadingCtrl.create({ content: this.translate.instant('LoginPage.loadingRequest') });
    loading.present();

    let object = {
      "mercazName": formValue.matnas,
      "userName": formValue.mail
    };

    this.api.auth('/reset-password/reset-password.php', object).then((response: any) => {
      loading.dismiss();
      if (response['sucsses']) {
        this.toast.presentToast(this.translate.instant('LoginPage.resetMailSent', { email: formValue.mail }));
        let alertBox = this.alertCtrl.create({
          title: this.translate.instant('LoginPage.resetTitle'),
          subTitle: this.translate.instant('LoginPage.resetSubtitle'),
          enableBackdropDismiss: false,
          inputs: [
            {
              name: 'ressetCode',
              placeholder: this.translate.instant('LoginPage.mailCodePlaceholder'),
              type: 'tel'
            },
            {
              name: 'newpassword',
              placeholder: this.translate.instant('LoginPage.newPasswordPlaceholder'),
              type: 'text'
            },
          ],
          buttons: [
            {
              text: this.translate.instant('COMMON.cancel'),
              role: 'cancel',
              handler: () => {
                console.log('Cancel clicked');
              }
            },
            {
              text: this.translate.instant('COMMON.continue'),
              handler: data => {
                var ressetCode = /^[0-9]{6}$/;
                const respregx = /^(?=.*[A-Z])(?=.*\d).{6,}$/;

                if (!data.ressetCode) {
                  this.toast.presentToast(this.translate.instant('LoginPage.enterMailCode'));
                  return false;
                }
                else if (!ressetCode.test(data.ressetCode)) {
                  this.toast.presentToast(this.translate.instant('LoginPage.invalidCode'));
                  return false;
                }
                else if (!data.newpassword) {
                  this.toast.presentToast(this.translate.instant('LoginPage.enterNewPassword'));
                  return false;
                }
                else if (!respregx.test(data.newpassword)) {
                  this.toast.presentToast(this.translate.instant('LoginPage.passwordRules'));
                  return false;
                }
                else {

                  let loading = this.loadingCtrl.create({ content: this.translate.instant('COMMON.loading') });
                  loading.present();
                  let object = {
                    "mercazName": formValue.matnas,
                    "userName": formValue.mail,
                    "mercazAllId": response['userData']['mercazAllId'],
                    "workerID": response['userData']['workerID'],
                    "server": response['userData']['server'],
                    "ressetCode": data.ressetCode,
                    "newpassword": data.newpassword
                  };

                  console.log("object:", object);

                  this.api.sendPostParams('/reset-password/step2.php', object).then((response2: any) => {
                    loading.dismiss();
                    if (response2['sucsses']) {
                      console.log("response2:", response2);
                      this.toast.presentToast(this.translate.instant('LoginPage.passwordChanged'));
                      this.toggleResetMode();
                    } else {
                      if (response2['error']) {
                        this.toast.presentToast(response2['error']);
                      } else {
                        this.toast.presentToast(this.translate.instant('LoginPage.genericError'));
                      }
                    }
                  }, (err2 => {
                    loading.dismiss();
                    console.log("error2:", JSON.stringify(err2));
                    this.toast.presentHttpError(err2);
                  }));
                }
              }
            },

          ]
        });
        alertBox.present();

      } else {
        this.toast.presentToast(this.translate.instant('LoginPage.resetMailError'));
      }
    }, (err => {
      loading.dismiss();
      console.log("Reset password error:", JSON.stringify(err));
      this.toast.presentHttpError(err);
    }));
  }

  registerPage() {
    const actionSheet = this.actionSheetCtrl.create({
      title: this.translate.instant('LoginPage.newUserTitle'),
      buttons: [
        {
          text: this.translate.instant('LoginPage.roleManager'),
          handler: () => {
            this.navCtrl.push('RegisterPage', {
              registerType: 0
            });
          }
        },
        {
          text: this.translate.instant('LoginPage.roleMentor'),
          handler: () => {
            this.navCtrl.push('RegisterPage', {
              registerType: 1
            });
          }
        },
        {
          text: this.translate.instant('COMMON.cancel'),
          role: 'cancel',
          handler: () => {
            console.log('Cancel clicked');
          }
        }
      ]
    });
    actionSheet.present();
  }

  visitAtarix() {
  }

  isIosDevice(): boolean {
    return this.iosDevice;
  }

  /**
   * Detect iOS including iPadOS, Cordova, and "Request Desktop Website" mode
   * (Mac-like UA but still touch-capable). Does not match real Mac desktops.
   */
  private detectIosDevice(): boolean {
    if (this.plt.is('ios')) {
      return true;
    }

    const ua = navigator.userAgent || navigator.vendor || '';
    const platform = navigator.platform || '';
    const maxTouchPoints = navigator.maxTouchPoints || 0;

    if (/iPad|iPhone|iPod/i.test(ua) || /iPad|iPhone|iPod/i.test(platform)) {
      return true;
    }

    // iPadOS 13+ reports as MacIntel
    if (platform === 'MacIntel' && maxTouchPoints > 1) {
      return true;
    }

    // iOS Safari/Chrome desktop mode: Macintosh UA but device still has touch
    const macLike = /Macintosh|MacIntel/i.test(ua) || platform === 'MacIntel';
    if (macLike && (maxTouchPoints > 0 || 'ontouchend' in document)) {
      return true;
    }

    return false;
  }

  goBackToPhoneStepOne(): void {
    this.phoneLoginStep = 1;
    this.loginForm.patchValue({ otpCode: '' });
    this.updateFormValidators();
  }

  ionViewDidLoad() {
    console.log('ionViewDidLoad LoginPage');
  }

}



// WEBPACK FOOTER //
// ./src/pages/login/login.ts